import { dev } from "@utils/dev";
import moment, { Moment } from "moment-timezone";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { ICleanChartValue } from "./CleanChart.control";

export type Enqueuer<T> = (
  timeInSec: number,
  value: T,
  tag?: string,
  id?: number
) => void;

export interface IChartLiveDataValue {
  timeInSec?: number;
  index: number;
  value: number;
}

export interface IChartCacheValue<T> {
  timeInSec: number;
  value: T;
  state: number;
}

export type AppendFunction<T> = (
  value: ICleanChartValue,
  countRef: MutableRefObject<number>
) => void;

export type DayChangedFunction = (
  date: Moment
) => void;

export interface IChartLiveDataSource<T> {
  start(
    appendToChart: AppendFunction<T>,
    onDayChanged: DayChangedFunction
  ): void;
  stop(): void;
  enqueue: Enqueuer<T>;
  setLastValue(value: T): void;
}

interface ChartDatasetCache<T> {
  [key: string]: IChartCacheValue<T>;
}

export type CleanChartValueConverter<T> = (timeInSec: number, value: T) => ICleanChartValue[];

export default function useChartLiveDataSource<T>(
  converter: CleanChartValueConverter<T>,
  onTickSecondListener?: (
    timeInSec: number,
    values: T[]
  ) => void,
): IChartLiveDataSource<T> {

  const TAG = "useChartLiveDataSource";

  const [isActive, setActive] = useState(false);
  const cacheRef = useRef<ChartDatasetCache<T>>({});
  const valuesCountRef = useRef(0);
  const prevValueRef = useRef<ICleanChartValue[] | null>(null); // для особого случая с нулем
  const prevDayRef = useRef<number>(); // для смены дня
  const appendFuncRef = useRef<AppendFunction<T>>();
  const dayChangedFuncRef = useRef<DayChangedFunction>();

  useEffect(() => {
    prevDayRef.current = moment().utc().day();
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (isActive) {
      interval = setInterval(() => {
        tickSecond(moment().utc().unix());
      }, 1000);
    } else {
      if (interval !== null) clearInterval(interval);
    }
    return () => {
      if (interval !== null) clearInterval(interval);
    }
  }, [isActive]);

  function tickSecond(timeInSec: number) {
    const dateMoment = moment(timeInSec * 1000).utc();
    const currentDay = dateMoment.day();
    if (prevDayRef.current !== currentDay) {
      //prevDayRef.current = currentDay;
      prevValueRef.current = null;
      valuesCountRef.current = 0;
      if (dayChangedFuncRef.current) {
        dayChangedFuncRef.current(dateMoment.startOf("day"));
      }
    } else {
      const values = getAllValuesFromCache(timeInSec);
      console.log("getAllValuesFromCache", values);
      if (values[0].length === 0) {
        // appendEmptyValues(timeInSec);
      } else {
        if (onTickSecondListener) {
          onTickSecondListener(timeInSec, values[1]);
        }
        appendValues(values[0]);
      }
    }
    prevDayRef.current = currentDay;
  };

  function getAllValuesFromCache(timeInSec: number): [Array<ICleanChartValue[]>, T[]] {
    const result: Array<ICleanChartValue[]> = [];
    const raw: T[] = [];
    for (const key in cacheRef.current) {
      const value = { ...cacheRef.current[key] };
      delete cacheRef.current[key];
      // костыль: timeInSec берем выше из таймера, 
      //          чтобы исключть закрашенных треугольников на графике
      raw.push(value.value);
      result.push(converter(timeInSec, value.value));
    }
    return [result, raw];
  }

  const appendValues = (
    values: Array<ICleanChartValue[]>
  ) => {
    console.log(TAG, "appendValues", { values });
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      prevValueRef.current = value;
      if (appendFuncRef.current) {
        for (let j = 0; j < value.length; j++) {
          appendFuncRef.current(value[j], valuesCountRef);
        }
      }
    }
  };

  const appendEmptyValues = (timeInSec: number) => {
    if (!appendFuncRef.current) return;
    if (prevValueRef.current === null) return;
    const prevValue = prevValueRef.current;
    for (let i = 0; i < prevValue.length; i++) {
      if (prevValue[i].datasetIndex === 0 && prevValue[i].value === 0) {
        for (let j = 0; j < prevValue.length; j++) {
          prevValue[j].timeInSec = timeInSec;
          appendFuncRef.current(prevValue[j], valuesCountRef);
        }
        break;
      }
    }
  }


  function getFieldName(time: number): string {
    return `field_${time}`;
  }

  const enqueue: Enqueuer<T> = (
    timeInSec: number,
    value: T,
  ) => {
    console.log(TAG, "enqueue", { timeInSec, value });
    if (timeInSec === 0) {
      if (appendFuncRef.current) {
        const v = converter(/*timeInSec*/ moment.utc().unix(), value);
        appendFuncRef.current(v[0], valuesCountRef);
      }
    } else {
      cacheRef.current[getFieldName(timeInSec)] = {
        timeInSec,
        value,
      } as IChartCacheValue<T>;
    }
  }

  function start(
    appendToChart: AppendFunction<T>,
    onDayChanged: DayChangedFunction,
  ) {
    appendFuncRef.current = appendToChart;
    dayChangedFuncRef.current = onDayChanged;
    setActive(true);
  }

  function stop() {
    appendFuncRef.current = undefined;
    dayChangedFuncRef.current = undefined;
    setActive(false);
  }

  return {
    start,
    stop,
    enqueue,
    setLastValue(value: T) {
      prevValueRef.current = converter(moment.utc().unix(), value);
    }
  }
}
